import { Box, Typography, Paper, Checkbox, IconButton } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import AppointmentConfig from './components/AppointmentConfig';
import AppointmentSchedulingConfig from './components/AppointmentSchedulingConfig';
import TransferConfig from './components/TransferConfig';
import DuringCallSmsConfig from './components/DuringCallSmsConfig';
import SpamConfig from './components/SpamConfig';
import { OrderBookingConfig } from './components/OrderBookingConfig';
import EmergencyCallHandlingConfig from './components/EmergencyCallHandlingConfig';
import { useParams } from 'react-router-dom';

export default function AgentCapabilitiesForm() {
  const { onboardingFormId } = useParams<{ onboardingFormId?: string; formId?: string }>();
  const { watch } = useFormContext();

  // 🔥 WATCH FULL FORM (BE STRUCTURE)
  const form = watch();
  const industry = watch('industry');

  const showAppointmentBookingSection =
    onboardingFormId && industry === 'healthcare' && form?.appointment_booking_enable;
  const showOrderBookingSection = onboardingFormId && industry === 'restaurant' && form?.order_booking_enable;

  return (
    <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: { xs: 2, md: 4 } }}>
      <Typography fontWeight={600} fontSize={18} mb={3}>
        Agent Capabilities
      </Typography>

      {showAppointmentBookingSection && (
        <CapabilityRow
          title='Appointment Booking'
          desc='Agent can book appointments with providers'
          enabled={form?.appointment_booking_enable}
          name='appointment_booking_enable'
        >
          <AppointmentConfig />
        </CapabilityRow>
      )}

      {/* RESTAURANT */}
      {showOrderBookingSection && (
        <CapabilityRow
          title='Order Booking'
          desc='Agent can take and process customer orders'
          enabled={form?.order_booking_enable}
          name='order_booking_enable'
        >
          <OrderBookingConfig />
        </CapabilityRow>
      )}

      <CapabilityRow
        title='Appointment Scheduling'
        desc='Agent can manage appointments by booking, updating, and canceling them with providers.'
        enabled={form?.appointment_scheduling_enable}
        name='appointment_scheduling_enable'
      >
        <AppointmentSchedulingConfig />
      </CapabilityRow>

      <CapabilityRow
        title='Transfer Call'
        desc='Agent can transfer calls to operators'
        enabled={form?.transfer_call_enable}
        name='transfer_call_enable'
      >
        <TransferConfig />
      </CapabilityRow>

      <CapabilityRow
        title='During Call: Send SMS to Caller'
        desc='Enable your agent to send SMS messages to callers during a call.'
        enabled={form?.send_sms_to_caller_enable}
        name='send_sms_to_caller_enable'
      >
        <DuringCallSmsConfig />
      </CapabilityRow>

      <SimpleRow
        title='Notify Business via SMS'
        desc='Send an SMS notification to the operator when the agent completes an action, such as scheduling an appointment or processing a refill request etc.'
        name='post_notify_via_sms_enable'
      />

      <SimpleRow
        title='Notify Business via Email'
        desc='Send an email notification to the operator when the agent completes an action, such as scheduling an appointment or processing a refill request etc.'
        name='post_notify_via_email_enable'
      />

      {/* NOTE TAKING */}
      <SimpleRow
        title='Take Note'
        desc='Agent will take a note if no staff member is available and the customer wants to speak with staff.'
        name='note_taking_enable'
      />

      {/* SPAM TERMINATION */}
      <CapabilityRow
        title='Spam Call Auto-Termination'
        desc='Detect spam calls and terminate instantly'
        enabled={form?.spam_call_termination_enable}
        name='spam_call_termination_enable'
      >
        <SpamConfig />
      </CapabilityRow>
      <CapabilityRow
        title='Emergency Call Handling'
        desc='Configure emergency call behavior'
        enabled={form?.emergency_call_handling_enable}
        name='emergency_call_handling_enable'
      >
        <EmergencyCallHandlingConfig />
      </CapabilityRow>
    </Paper>
  );
}

function SimpleRow({ title, desc, name }: any) {
  const { control } = useFormContext();

  return (
    <Paper variant='outlined' sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
      <Box display='flex' alignItems='center' gap={2}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Checkbox checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />
          )}
        />
        <Box>
          <Typography fontWeight={500}>{title}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {desc}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function CapabilityRow({ title, desc, enabled, name, children }: any) {
  const { control } = useFormContext();

  return (
    <Paper variant='outlined' sx={{ p: 2.5, mb: 2 }}>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Box display='flex' alignItems='center' gap={2}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Checkbox checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />
            )}
          />

          <Box>
            <Typography fontWeight={500}>{title}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {desc}
            </Typography>
          </Box>
        </Box>

        <IconButton>{enabled ? <IoChevronUp /> : <IoChevronDown />}</IconButton>
      </Box>

      {enabled && <Box mt={3}>{children}</Box>}
    </Paper>
  );
}
